/**
 * OtpChallenge — 6-digit OTP entry screen.
 *
 * Two modes:
 *   • Login 2FA:    verifyPath="/auth/verify-otp"          body={ username, otp }
 *   • Register:     verifyPath="/auth/register/verify-otp" body={ email, otp }
 *
 * Both call the shared `resendPath` (defaults to `/auth/resend-otp` for login,
 * `/auth/register/send-verify-otp` for registration).
 */
import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Loader2, ShieldCheck, Mail } from "lucide-react";

const LEN = 6;

export default function OtpChallenge({
  username,
  email,
  emailHint,
  onVerified,
  onCancel,
  verifyPath = "/auth/verify-otp",
  resendPath,
  title = "Enter code",
  subtitle,
}) {
  const [digits, setDigits] = useState(Array(LEN).fill(""));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [cooldown, setCooldown] = useState(60);
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const setDigit = (i, v) => {
    v = v.replace(/\D/g, "").slice(0, 1);
    setDigits((d) => {
      const next = [...d];
      next[i] = v;
      return next;
    });
    if (v && i < LEN - 1) inputs.current[i + 1]?.focus();
  };

  const onKey = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
    if (e.key === "Enter") verify();
  };

  const onPaste = (e) => {
    const t = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, LEN);
    if (!t) return;
    e.preventDefault();
    const next = Array(LEN).fill("");
    for (let i = 0; i < t.length; i++) next[i] = t[i];
    setDigits(next);
    inputs.current[Math.min(t.length, LEN - 1)]?.focus();
  };

  const verify = async () => {
    const otp = digits.join("");
    if (otp.length !== LEN) { setErr("Enter all 6 digits."); return; }
    setBusy(true); setErr("");
    try {
      const body = email ? { email, otp } : { username, otp };
      const { data } = await api.post(verifyPath, body);
      if (!data?.verified && !data?.ok) throw new Error("Verification failed.");
      // Pass either the JWT (login) OR the email_verified_token (register) upward.
      onVerified?.(data);
    } catch (ex) {
      setErr(apiError(ex));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    setBusy(true); setErr("");
    try {
      const fallback = email ? "/auth/register/send-verify-otp" : "/auth/resend-otp";
      const body = email ? { email } : { username };
      await api.post(resendPath || fallback, body);
      setCooldown(60);
      setDigits(Array(LEN).fill(""));
      inputs.current[0]?.focus();
    } catch (ex) {
      setErr(apiError(ex));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="otp-page"
         style={{ background: "linear-gradient(180deg, #0b0b0d 0%, #14141a 100%)" }}>
      <div className="w-full max-w-md text-center" style={{ background: "rgba(20,20,26,0.9)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 28 }}>
        <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4"
             style={{ background: "rgba(245,130,32,0.18)", border: "1px solid rgba(245,130,32,0.4)", color: "var(--primary)" }}>
          <ShieldCheck className="w-7 h-7" />
        </div>
        <div className="font-display font-black text-2xl">{title}</div>
        <div className="text-sm text-muted mt-2 flex items-center justify-center gap-1.5">
          <Mail className="w-3.5 h-3.5" /> {subtitle || "Code sent to"} <span className="text-white font-mono-tight">{emailHint || "your email"}</span>
        </div>

        <div className="flex justify-center gap-2 my-6" onPaste={onPaste}>
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => (inputs.current[i] = el)}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKey(i, e)}
              inputMode="numeric"
              maxLength={1}
              data-testid={`otp-digit-${i}`}
              className="text-center font-mono-tight font-black text-2xl"
              style={{
                width: 48, height: 60,
                background: "rgba(0,0,0,0.4)",
                border: `2px solid ${d ? "var(--primary)" : "rgba(255,255,255,0.12)"}`,
                borderRadius: 10,
                color: "#fff",
                outline: "none",
              }}
            />
          ))}
        </div>

        {err && (
          <div className="pill pill-red !text-sm !py-2 !px-3 mb-4" data-testid="otp-error">{err}</div>
        )}

        <button
          className="btn btn-primary w-full !text-base !py-3"
          onClick={verify}
          disabled={busy}
          data-testid="otp-verify"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Verify
        </button>

        <button
          className="text-[12px] mt-4 disabled:opacity-40"
          onClick={resend}
          disabled={cooldown > 0 || busy}
          data-testid="otp-resend"
          style={{ color: cooldown > 0 ? "rgba(255,255,255,0.4)" : "var(--primary)" }}
        >
          {cooldown > 0 ? `Resend code (${cooldown}s)` : "Resend code"}
        </button>

        {onCancel && (
          <div className="mt-3">
            <button
              className="text-[11px] uppercase tracking-widest text-muted hover:text-white transition-colors"
              onClick={onCancel}
              disabled={busy}
              data-testid="otp-cancel"
            >
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
